import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  VStack,
  HStack,
  Input,
  Card,
  Text,
  IconButton,
  Progress,
} from '@chakra-ui/react';
import { FiSend, FiPaperclip } from 'react-icons/fi';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

function ChatPage() {
  const { sessionId: _sessionId } = useParams();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content:
        "Hello! I'm here to help you create a quote for your WordPress website. What kind of website are you looking to build?",
      timestamp: new Date().toISOString(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content:
          "That sounds interesting! Can you tell me more about your business and what features you'd like on the website?",
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, aiMessage]);
      setIsTyping(false);
    }, 1500);
  };

  return (
    <Box h="calc(100vh - 200px)" display="flex" flexDirection="column">
      {/* Progress Bar */}
      <Box mb={4}>
        <Text fontSize="sm" color="gray.600" mb={2}>
          Gathering Requirements - 25% Complete
        </Text>
        <Progress.Root value={25} colorPalette="brand" size="sm">
          <Progress.Track>
            <Progress.Range />
          </Progress.Track>
        </Progress.Root>
      </Box>

      {/* Messages Area */}
      <Card.Root flex={1} overflow="hidden">
        <Card.Body>
          <VStack
            gap={4}
            align="stretch"
            h="full"
            overflowY="auto"
            px={2}
          >
            {messages.map((message) => (
              <Box
                key={message.id}
                alignSelf={message.role === 'user' ? 'flex-end' : 'flex-start'}
                maxW="70%"
              >
                <Box
                  bg={message.role === 'user' ? 'brand.500' : 'gray.100'}
                  color={message.role === 'user' ? 'white' : 'black'}
                  px={4}
                  py={3}
                  borderRadius="lg"
                >
                  <Text>{message.content}</Text>
                </Box>
                <Text
                  fontSize="xs"
                  color="gray.500"
                  mt={1}
                  textAlign={message.role === 'user' ? 'right' : 'left'}
                >
                  {new Date(message.timestamp).toLocaleTimeString()}
                </Text>
              </Box>
            ))}

            {isTyping && (
              <Box alignSelf="flex-start" maxW="70%">
                <Box bg="gray.100" px={4} py={3} borderRadius="lg">
                  <Text>AI is typing...</Text>
                </Box>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </VStack>
        </Card.Body>
      </Card.Root>

      {/* Input Area */}
      <Box pt={4}>
        <HStack gap={2}>
          <IconButton aria-label="Attach file" variant="ghost">
            <FiPaperclip />
          </IconButton>
          <Input
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            flex={1}
          />
          <Button colorPalette="brand" onClick={handleSend}>
            <FiSend />
            Send
          </Button>
        </HStack>

        <HStack gap={2} mt={2}>
          <Button size="sm" variant="outline">
            Upload Files
          </Button>
          <Button size="sm" variant="outline">
            Add Website URL
          </Button>
          <Button size="sm" variant="outline">
            View Examples
          </Button>
        </HStack>
      </Box>
    </Box>
  );
}

export default ChatPage;